/* 
Currently this modal is focused on managing image assets for gameplay sync,
but this functionality has been disabled for the time being. 
The code is left here for future reference and potential reuse 
when we re-enable gameplay sync or add other asset management features.
*/

import { useState, useEffect, useCallback, useRef } from 'react';
import Rodal from 'rodal';
import { Button } from '../../../../shared/components/Button';
import '../../../../shared/styles/Orchestrator.css';
import 'rodal/lib/rodal.css';
import {
  UploadCloud,
  Download,
  Upload,
  CloudSync,
  CloudCheck,
  CloudAlert,
  LogIn,
  LogOut,
} from 'lucide-react';
import { getImage } from '../../utils/storage';
import {
  uploadToStorage,
  getAssetUrlMap,
  setAssetUrl,
  connectToStorage,
  disconnectFromStorage,
  onStorageAuthChange,
  getPersistedStorageAuth,
} from '../../utils/assetUpload';

const PREVIEW_LOAD_TIMEOUT_MS = 10000;

function collectSceneAssets(scenes) {
  const seen = new Set();
  const assets = [];

  for (const scene of scenes) {
    for (const layer of scene.layers || []) {
      if (layer.imageId && !seen.has(layer.imageId)) {
        seen.add(layer.imageId);
        assets.push({
          key: `${scene.id}_layer_${layer.id}`,
          imageId: layer.imageId,
          label: `${scene.name} › ${layer.name || 'Layer'}`,
        });
      }
    }

    for (const token of scene.tokens || []) {
      const pairs = [
        { id: token.goodImageId || token.imageId, suffix: 'good' },
        { id: token.evilImageId || token.altImageId, suffix: 'evil' },
        { id: token.neutralImageId, suffix: 'neutral' },
      ];
      for (const { id, suffix } of pairs) {
        if (id && !seen.has(id)) {
          seen.add(id);
          assets.push({
            key: `${scene.id}_token_${token.id}_${suffix}`,
            imageId: id,
            label: `${scene.name} › ${token.name || 'Token'} (${suffix})`,
          });
        }
      }
    }
  }

  return assets;
}

// ---------------------------------------------------------------------------
// Single asset row
// ---------------------------------------------------------------------------

function AssetRow({ asset, urlMap, dataUrl, status }) {
  const url = urlMap[asset.imageId];
  const isDone = !!url;
  const isUploading = status === 'uploading';
  const isError = !!status && status !== 'uploading';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 4px',
        borderBottom: '1px solid rgba(128,128,128,0.15)',
      }}
    >
      {/* Thumbnail */}
      <div
        style={{
          width: 34,
          height: 34,
          flexShrink: 0,
          borderRadius: 4,
          overflow: 'hidden',
          background: 'rgba(128,128,128,0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {dataUrl ? (
          <img
            src={dataUrl}
            alt=''
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <span style={{ fontSize: 10, opacity: 0.4 }}>…</span>
        )}
      </div>

      {/* Label */}
      <div
        style={{
          flex: 1,
          fontSize: 12,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {asset.label}
      </div>

      {/* Status / link */}
      <div
        style={{
          flexShrink: 0,
          fontSize: 11,
          minWidth: 90,
          textAlign: 'right',
        }}
      >
        {isUploading ? (
          <span style={{ opacity: 0.6 }}>Uploading…</span>
        ) : isError ? (
          <span style={{ color: '#e94f37' }} title={status}>
            Error
          </span>
        ) : isDone ? (
          <a
            href={url}
            target='_blank'
            rel='noreferrer'
            title={url}
            style={{
              color: '#4caf50',
              textDecoration: 'none',
              fontWeight: 600,
            }}
          >
            ✓ Uploaded
          </a>
        ) : (
          <span style={{ opacity: 0.5 }}>Not uploaded</span>
        )}
      </div>
    </div>
  );
}

export default function AssetManagerModal({
  visible,
  mode,
  scenes = [],
  onClose,
  onExportWithUrls,
  onImport,
  isGameplaySyncEnabled = false,
  onEnableGameplaySync,
  onDisableGameplaySync,
  isSyncing = false,
}) {
  const isDark = mode === 'dark';
  // Storage auth state — seeded from localStorage for instant render, kept in sync via onStorageAuthChange
  const [storageAuthUser, setStorageAuthUser] = useState(() => getPersistedStorageAuth());
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectError, setConnectError] = useState(null);
  const [urlMap, setUrlMap] = useState({});
  // imageId → 'uploading' | error-message-string | null
  const [statuses, setStatuses] = useState({});
  const [dataUrls, setDataUrls] = useState({});
  const [isLoadingPreviews, setIsLoadingPreviews] = useState(false);
  const [previewProgress, setPreviewProgress] = useState({ done: 0, total: 0 });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const abortRef = useRef(false);

  const assets = collectSceneAssets(scenes);
  const uploadedCount = assets.filter((a) => !!urlMap[a.imageId]).length;
  const pendingCount = assets.filter((a) => !urlMap[a.imageId]).length;

  const [isPhone, setIsPhone] = useState(false);

  // Track Firebase Auth state
  useEffect(() => {
    const unsub = onStorageAuthChange((user) => {
      setStorageAuthUser(
        user
          ? { uid: user.uid, email: user.email, displayName: user.displayName }
          : null
      );
    });
    return unsub;
  }, []);

  const handleConnect = async () => {
    setIsConnecting(true);
    setConnectError(null);
    try {
      await connectToStorage();
    } catch (err) {
      setConnectError(err.message || 'Sign-in failed. Please try again.');
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    await disconnectFromStorage();
  };

  useEffect(() => {
    const mediaQueryWidth = window.matchMedia('(max-width: 655px)');
    const handleMediaChange = () => {
      setIsPhone(mediaQueryWidth.matches);
    };
    handleMediaChange();
    mediaQueryWidth.addListener(handleMediaChange);
    return () => {
      mediaQueryWidth.removeListener(handleMediaChange);
    };
  }, []);

  // Reload URL map and thumbnails every time the modal opens
  useEffect(() => {
    if (!visible) return;
    abortRef.current = false;
    setUrlMap(getAssetUrlMap());
    setDataUrls({});

    const collected = collectSceneAssets(scenes);

    const getImageWithTimeout = (
      imageId,
      timeoutMs = PREVIEW_LOAD_TIMEOUT_MS,
    ) =>
      Promise.race([
        getImage(imageId),
        new Promise((resolve) => {
          setTimeout(() => resolve(null), timeoutMs);
        }),
      ]);

    const load = async () => {
      setIsLoadingPreviews(true);
      setPreviewProgress({ done: 0, total: collected.length });

      for (let i = 0; i < collected.length; i++) {
        if (abortRef.current) break;
        const asset = collected[i];

        try {
          const dataUrl = await getImageWithTimeout(asset.imageId);
          if (dataUrl && !abortRef.current) {
            setDataUrls((prev) => ({ ...prev, [asset.imageId]: dataUrl }));
          }
        } catch {
          // non-fatal: missing/corrupt image should not block modal loading
        }

        if (!abortRef.current) {
          setPreviewProgress({ done: i + 1, total: collected.length });
        }
      }

      if (!abortRef.current) {
        setIsLoadingPreviews(false);
      }
    };
    load();

    return () => {
      abortRef.current = true;
      setIsLoadingPreviews(false);
    };
  }, [visible, scenes]);

  const uploadAll = useCallback(async () => {
    const toUpload = assets.filter((a) => !urlMap[a.imageId]);
    if (!toUpload.length || isUploading) return;

    setIsUploading(true);
    abortRef.current = false;
    setUploadProgress({ done: 0, total: toUpload.length });

    for (let i = 0; i < toUpload.length; i++) {
      if (abortRef.current) break;
      const asset = toUpload[i];

      setStatuses((prev) => ({ ...prev, [asset.imageId]: 'uploading' }));

      try {
        let dataUrl = dataUrls[asset.imageId];
        if (!dataUrl) dataUrl = await getImage(asset.imageId);
        if (!dataUrl) throw new Error('Image data not found');

        const remoteUrl = await uploadToStorage(dataUrl);
        setAssetUrl(asset.imageId, remoteUrl);
        setUrlMap((prev) => ({ ...prev, [asset.imageId]: remoteUrl }));
        setStatuses((prev) => ({ ...prev, [asset.imageId]: null }));
      } catch (err) {
        console.error('[AssetManager] Upload failed for', asset.imageId, err);
        setStatuses((prev) => ({
          ...prev,
          [asset.imageId]: err.message || 'Upload failed',
        }));
      }

      setUploadProgress({ done: i + 1, total: toUpload.length });
    }

    setIsUploading(false);
  }, [assets, urlMap, dataUrls, isUploading]);

  const borderColor = isDark ? '2px solid var(--onyx)' : '2px solid #e4E7C5';
  const listBorderColor = isDark
    ? '1px solid var(--onyx)'
    : '1px solid #dcdcdc';

  return (
    <Rodal
      animation='slideUp'
      visible={visible}
      onClose={onClose}
      className={isDark ? 'df_rodal_dark' : ''}
      customStyles={{
        border: borderColor,
        backgroundColor: isDark ? 'var(--jet)' : '#f6f7eb',
        color: isDark ? 'var(--ivory)' : 'var(--jet)',
        width: isPhone ? 'calc(100% - 34px)' : '620px',
        height: isPhone ? 'calc(100% - 114px)' : '580px',
        marginTop: isPhone ? '80px' : '',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        className={`trialment_login_form ${isDark ? 'df_modal_form_dark' : ''}`}
        style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        {/* Header */}
        <div
          style={{
            fontWeight: 700,
            fontSize: 16,
            marginBottom: 6,
            textAlign: 'center',
          }}
        >
          Gameplay Sync
        </div>

        {/* Storage auth banner */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 10px',
            borderRadius: 6,
            marginBottom: 10,
            background: storageAuthUser
              ? 'rgba(76, 175, 80, 0.12)'
              : 'rgba(233, 79, 55, 0.10)',
            border: storageAuthUser
              ? '1px solid rgba(76, 175, 80, 0.35)'
              : '1px solid rgba(233, 79, 55, 0.35)',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: 1, fontSize: 12 }}>
            {storageAuthUser ? (
              <>
                <span style={{ fontWeight: 700, color: '#4caf50' }}>
                  Connected to Storage
                </span>
                {storageAuthUser.email && (
                  <span style={{ opacity: 0.7, marginLeft: 6 }}>
                    {storageAuthUser.email}
                  </span>
                )}
              </>
            ) : (
              <span style={{ opacity: 0.8 }}>
                Sign in with Google to upload assets to Firebase Storage.
              </span>
            )}
          </div>
          {storageAuthUser ? (
            <Button
              icon={LogOut}
              label='Disconnect'
              type='onyx'
              onPress={handleDisconnect}
            />
          ) : (
            <Button
              icon={LogIn}
              label={isConnecting ? 'Signing in…' : 'Connect with Google'}
              type='cinnabar'
              disabled={isConnecting}
              onPress={handleConnect}
            />
          )}
        </div>
        {connectError && (
          <div
            style={{
              fontSize: 12,
              color: '#e94f37',
              marginBottom: 8,
              marginTop: -6,
            }}
          >
            {connectError}
          </div>
        )}

        {/* Summary */}
        <div style={{ fontSize: 12, marginBottom: 10, opacity: 0.7 }}>
          {assets.length === 0
            ? 'No images found in scenes'
            : `${uploadedCount} / ${assets.length} images uploaded`}
          {assets.length > 0 && isLoadingPreviews
            ? ` • Loading previews ${previewProgress.done}/${previewProgress.total}`
            : ''}
        </div>

        {/* Asset list */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            border: listBorderColor,
            borderRadius: 6,
            padding: '0 8px',
          }}
        >
          {assets.length === 0 ? (
            <div style={{ padding: 16, opacity: 0.6, fontSize: 13 }}>
              Images you add to scenes will appear here and can be uploaded.
              Uploading the images is necessary before gameplay information can
              be synchronized in real time.
            </div>
          ) : (
            assets.map((asset) => (
              <AssetRow
                key={asset.key}
                asset={asset}
                urlMap={urlMap}
                dataUrl={dataUrls[asset.imageId]}
                status={statuses[asset.imageId] || null}
              />
            ))
          )}
        </div>

        {/* Upload control */}
        <div
          style={{
            marginTop: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            icon={UploadCloud}
            label={
              isUploading
                ? `Uploading… ${uploadProgress.done}/${uploadProgress.total}`
                : pendingCount === 0
                  ? 'All Assets Uploaded'
                  : `Upload All Assets (${pendingCount})`
            }
            type={isDark ? 'onyx' : 'cinnabar'}
            disabled={isUploading || pendingCount === 0 || !storageAuthUser}
            onPress={uploadAll}
            {...(!storageAuthUser && { description: 'Connect to Google to upload' })}
          />
          {isUploading && (
            <span style={{ fontSize: 11, opacity: 0.6 }}>
              Uploading images — please wait…
            </span>
          )}
        </div>

        {/* Import / Export */}
        <div
          style={{
            marginTop: '8px',
            marginBottom: '8px',
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            icon={Download}
            label='Import'
            type={isDark ? 'onyx' : 'tomato'}
            onPress={onImport}
          />
          <Button
            icon={Upload}
            label='Export with URLs'
            type={isDark ? 'onyx' : 'tomato'}
            disabled={uploadedCount === 0}
            onPress={onExportWithUrls}
          />
          <Button
            icon={
              isSyncing
                ? CloudSync
                : isGameplaySyncEnabled
                  ? CloudCheck
                  : pendingCount === 0
                    ? CloudSync
                    : CloudAlert
            }
            label={isGameplaySyncEnabled ? 'Disable Gameplay Sync' : 'Enable Gameplay Sync'}
            type={isGameplaySyncEnabled ? 'tomato' : 'cinnabar'}
            disabled={!isGameplaySyncEnabled && pendingCount !== 0}
            onPress={() => {
              if (isGameplaySyncEnabled) {
                if (typeof onDisableGameplaySync === 'function') onDisableGameplaySync();
              } else {
                if (typeof onEnableGameplaySync === 'function') onEnableGameplaySync();
              }
            }}
            description={
              !isGameplaySyncEnabled && pendingCount !== 0
                ? 'Upload All Assets to Enable Sync'
                : undefined
            }
            descriptionUp={isPhone ? true : false}
          />
        </div>
      </div>
    </Rodal>
  );
}

